import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Calculator, FolderKanban, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { ExternalCompanyPicker } from "@/components/shared/ExternalCompanyPicker";
import { useCreateProposal } from "../hooks/useProposals";
import { useEstimationList } from "../hooks/useEstimations";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (id: string, code: string) => void;
  /** Pre-fill the recipient (client) company picker. Phase 1+2: opened from
   *  External company's Proposals tab. */
  defaultRecipientCompanyId?: string;
};

export function NewProposalDrawer({
  isOpen,
  onClose,
  onCreated,
  defaultRecipientCompanyId,
}: Props) {
  const createProposal = useCreateProposal();
  const { data: currencyItems = [] } = useCategoryItems("currency");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clientCompanyId, setClientCompanyId] = useState(
    defaultRecipientCompanyId ?? "",
  );
  const [projectId, setProjectId] = useState("");
  const [estimationId, setEstimationId] = useState("");
  const [currency, setCurrency] = useState("AED");
  const [validUntil, setValidUntil] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [taxPct, setTaxPct] = useState("5");
  const [taxIncluded, setTaxIncluded] = useState(false);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setClientCompanyId(defaultRecipientCompanyId ?? "");
    setError(null);
  }, [isOpen, defaultRecipientCompanyId]);

  // Filter projects by external client (Phase 1+2 axis), NOT by operational
  // companyId — those are separate fields post-Phase-3.
  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    clientCompanyId: clientCompanyId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: estimationsResult } = useEstimationList({
    page: 1,
    limit: 100,
    companyId: clientCompanyId || undefined,
  });
  const estimations = estimationsResult?.data ?? [];

  function reset() {
    setName("");
    setDescription("");
    setClientCompanyId("");
    setProjectId("");
    setEstimationId("");
    setCurrency("AED");
    setValidUntil("");
    setDiscountPct("");
    setTaxPct("5");
    setTaxIncluded(false);
    setNotes("");
    setTerms("");
    setError(null);
    setWarning(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (!clientCompanyId) {
      setError("Client company is required");
      return;
    }
    setError(null);
    try {
      const discount = discountPct.trim() === "" ? null : Number(discountPct);
      const tax = taxPct.trim() === "" ? null : Number(taxPct);

      const res = await createProposal.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        clientCompanyId,
        projectId: projectId || null,
        estimationId: estimationId || null,
        currency,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        discountPct: discount,
        taxPct: tax,
        taxIncluded,
        notes: notes.trim() || null,
        terms: terms.trim() || null,
      });
      onCreated?.(res.proposal.id, res.proposal.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create proposal");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">
            New Price Proposal
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">Details</h3>

            <div className="space-y-2">
              <Label htmlFor="prp-name">
                Proposal Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="prp-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Accenture Q3 Proposal"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prp-desc">Description</Label>
              <textarea
                id="prp-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm"
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Association
            </h3>

            <div className="space-y-2">
              <Label htmlFor="prp-client">
                Client Company <span className="text-destructive">*</span>
              </Label>
              <ExternalCompanyPicker
                id="prp-client"
                value={clientCompanyId}
                onChange={(v) => {
                  // If the currently-selected project is for a different
                  // client, drop it and warn the user — keeps the proposal
                  // from being saved with mismatched client/project.
                  if (projectId) {
                    const proj = projects.find((p) => p.id === projectId);
                    if (proj && proj.clientCompanyId !== v) {
                      setProjectId("");
                      setWarning(
                        `Cleared project "${proj.name}" — it belongs to a different client.`,
                      );
                      window.setTimeout(() => setWarning(null), 4000);
                    }
                  }
                  setClientCompanyId(v);
                  setEstimationId("");
                }}
                systemCodes={["client", "customer"]}
                placeholder="Select client company…"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prp-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="prp-project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prp-estimation">Linked Estimation</Label>
              <div className="relative">
                <Calculator
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="prp-estimation"
                  value={estimationId}
                  onChange={(e) => setEstimationId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm"
                >
                  <option value="">No linked estimation</option>
                  {estimations.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.code} — {e.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Pricing</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prp-currency">Currency</Label>
                <Select
                  id="prp-currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {currencyItems.length === 0 ? (
                    <option value="AED">AED</option>
                  ) : (
                    currencyItems.map((c) => (
                      <option key={c.id} value={c.value.toUpperCase()}>
                        {c.label}
                      </option>
                    ))
                  )}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prp-valid">Valid Until</Label>
                <Input
                  id="prp-valid"
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prp-discount">Discount %</Label>
                <Input
                  id="prp-discount"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prp-tax">Tax %</Label>
                <Input
                  id="prp-tax"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={taxPct}
                  onChange={(e) => setTaxPct(e.target.value)}
                />
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={taxIncluded}
                onChange={(e) => setTaxIncluded(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              Tax included in prices
            </label>

            <div className="space-y-2">
              <Label htmlFor="prp-notes">Notes</Label>
              <textarea
                id="prp-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prp-terms">Payment Terms</Label>
              <textarea
                id="prp-terms"
                rows={3}
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                placeholder="e.g. 50% on signing, 50% on delivery"
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm"
              />
            </div>

            {warning && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(38,92%,75%)",
                  background: "hsl(38,92%,96%)",
                  color: "hsl(38,92%,30%)",
                }}
              >
                {warning}
              </div>
            )}
            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createProposal.isPending}
            >
              {createProposal.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Proposal
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
